import { FocusMonitor, FocusOrigin } from '@angular/cdk/a11y';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  ViewChild,
  ViewEncapsulation,
  inject,
  signal,
} from '@angular/core';
import { classnames } from '@fmnts/common';
import { htmlIdMaker } from '@fmnts/components/core';
import { AbstractRadioButton } from './base-radio-button.directive';
import { FmntsRadioButton } from './radio.model';

const radioIds = htmlIdMaker('fmnts-radio-button');

@Component({
  selector: 'fmnts-radio-button',
  templateUrl: './radio-button.component.html',
  styleUrls: ['./radio-button.component.scss'],
  imports: [],
  providers: [
    {
      provide: FmntsRadioButton,
      useExisting: RadioButtonComponent,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    // Needs to be removed since it causes some a11y issues.
    '[attr.tabindex]': 'null',
    '[attr.aria-label]': 'null',
    '[attr.aria-labelledby]': 'null',
    '[attr.aria-describedby]': 'null',
  },
})
export class RadioButtonComponent
  extends AbstractRadioButton<unknown>
  implements AfterViewInit
{
  private readonly _focusMonitor = inject(FocusMonitor);
  protected readonly _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  @HostBinding('class.fmnts-radio-button') protected componentClass =
    'fmnts-radio-button';

  override readonly _id = signal(radioIds.next().value);

  /** The native `<input type=radio>` element */
  @ViewChild('input') _inputElement!: ElementRef<HTMLInputElement>;

  @HostBinding('class') get hostClasses(): string {
    return classnames([
      this.disabled && `${this.componentClass}--disabled`,
      this.checked && `${this.componentClass}--checked`,
    ]);
  }

  ngAfterViewInit(): void {
    this._focusMonitor
      .monitor(this._elementRef, true)
      .subscribe((focusOrigin) => {
        if (!focusOrigin) {
          this._emitRadioGroupTouched();
        }
      });
    this._destroyRef.onDestroy(() => {
      this._focusMonitor.stopMonitoring(this._elementRef);
    });
  }

  @HostListener('focus')
  protected override focus(options?: FocusOptions, origin?: FocusOrigin): void {
    if (origin) {
      this._focusMonitor.focusVia(this._inputElement, origin, options);
    } else {
      this._inputElement.nativeElement.focus(options);
    }
  }

  /** Triggered when the radio button receives an interaction from the user. */
  protected _onInputInteraction(event: Event): void {
    // We always have to stop propagation on the change event.
    // Otherwise the change event, from the input element, will bubble up and
    // emit its event object to the `change` output.
    event.stopPropagation();

    this._select();
  }

  /** Triggered when the user clicks on the touch target. */
  protected _onTouchTargetClick(event: Event): void {
    this._onInputInteraction(event);

    if (!this.disabled) {
      this.focus();
    }
  }
}
